import { parseScreenDraft, type ScreenDraftSchema } from './screen-context.js';

/**
 * RASCUNHO: HASH CANONICO E CONTRATO DO PATCH ("faca por mim", A4).
 *
 * Este arquivo e o ACORDO entre o servidor, o painel e as paginas sobre como um
 * rascunho de tela e alterado pelo Rimati. NADA aqui altera uma tela: a A4
 * implementa o `useDraftPatch` da web e a ferramenta `proporAlteracaoNoRascunho`
 * do servidor sobre estes tipos. Documento: docs/agente-nas-telas/contrato-draft-patch.md.
 */

/* -------------------------------------------------------------------------- */
/* Hash canonico (o mesmo no navegador e no servidor)                          */
/* -------------------------------------------------------------------------- */

/** JSON com chaves em ordem de codigo: o mesmo valor sempre vira o mesmo texto. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`)
      .join(',')}}`;
  return JSON.stringify(value) ?? 'null';
}

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** UTF-8 sem depender de `TextEncoder` (o pacote nao assume DOM nem Node). */
function utf8Bytes(text: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    let code = text.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      const low = text.charCodeAt(i + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (low - 0xdc00);
        i += 1;
      }
    }
    if (code < 0x80) out.push(code);
    else if (code < 0x800) out.push(0xc0 | (code >> 6), 0x80 | (code & 63));
    else if (code < 0x10000)
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 63), 0x80 | (code & 63));
    else
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 63),
        0x80 | ((code >> 6) & 63),
        0x80 | (code & 63),
      );
  }
  return Uint8Array.from(out);
}

/** SHA-256 em JavaScript puro (sem `crypto`): igual no navegador e no Node. */
export function sha256Hex(text: string): string {
  const bytes = utf8Bytes(text);
  const length = bytes.length;
  const padded = new Uint8Array(((length + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor((length * 8) / 0x100000000));
  view.setUint32(padded.length - 4, (length * 8) >>> 0);
  const h = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w[i] = view.getUint32(offset + i * 4);
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15]!, 7) ^ rotr(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3);
      const s1 = rotr(w[i - 2]!, 17) ^ rotr(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let a = h[0]!;
    let b = h[1]!;
    let c = h[2]!;
    let d = h[3]!;
    let e = h[4]!;
    let f = h[5]!;
    let g = h[6]!;
    let hh = h[7]!;
    for (let i = 0; i < 64; i += 1) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + s1 + ch + K[i]! + w[i]!) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    const next = [a, b, c, d, e, f, g, hh];
    for (let i = 0; i < 8; i += 1) h[i] = (h[i]! + next[i]!) >>> 0;
  }
  return h.map((word) => word.toString(16).padStart(8, '0')).join('');
}

/** O hash de um rascunho: sha256 do JSON canonico. */
export const draftHash = (draft: unknown): string => sha256Hex(canonicalJson(draft));

/**
 * O hash BASE de um rascunho: sempre sobre a forma normalizada pelo esquema
 * (o que o servidor recebe depois de `parseScreenDraft`). Pagina e servidor
 * usam ESTA funcao; nunca o rascunho resolvido pelo servidor. `null` se o
 * rascunho nao passa no esquema.
 */
export function draftBaseHash(schema: ScreenDraftSchema, raw: unknown): string | null {
  const parsed = parseScreenDraft(schema, raw);
  return parsed.ok ? draftHash(parsed.value.data) : null;
}

/* -------------------------------------------------------------------------- */
/* Contrato do patch                                                           */
/* -------------------------------------------------------------------------- */

/** Tempo que o painel espera a pagina confirmar (aplicar ou recusar) um patch. */
export const DRAFT_PATCH_ACK_TIMEOUT_MS = 5000;

/** Subconjunto do RFC 6902 que um rascunho precisa. Caminhos em JSON Pointer. */
export type DraftPatchOp =
  | { op: 'add'; path: string; value: unknown }
  /** `expect` e o valor que o servidor VIU (como o `test` do RFC 6902): base do rebase por campo. */
  | { op: 'replace'; path: string; value: unknown; expect?: unknown }
  | { op: 'remove'; path: string; expect?: unknown };

/** Dados que a pagina nao tem e o servidor ja conferiu (linhas novas, nome do cliente). */
export interface DraftPatchHints {
  /** Por `variantId`: o que a pagina precisa para criar a linha no carrinho. */
  lines?: Record<
    string,
    { productId: string; name: string; label: string; unitCents: number; revision: number }
  >;
  customerName?: string;
}

/* -------------------------------------------------------------------------- */
/* JSON Pointer (subconjunto do RFC 6901/6902)                                 */
/* -------------------------------------------------------------------------- */

/** Copia profunda de valores JSON (o pacote nao assume DOM nem Node). */
export function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

const unescapePointer = (part: string): string => part.replace(/~1/g, '/').replace(/~0/g, '~');

function pointerParts(path: string): string[] | null {
  if (!path.startsWith('/')) return null;
  return path.slice(1).split('/').map(unescapePointer);
}

/** Le o valor no caminho; `undefined` se nao existe. */
export function getAtPointer(doc: unknown, path: string): unknown {
  const parts = pointerParts(path);
  if (!parts) return undefined;
  let current: unknown = doc;
  for (const part of parts) {
    if (Array.isArray(current)) {
      if (!/^(0|[1-9]\d*)$/.test(part)) return undefined;
      current = current[Number(part)];
    } else if (current && typeof current === 'object') {
      if (!Object.hasOwn(current, part)) return undefined;
      current = (current as Record<string, unknown>)[part];
    } else return undefined;
  }
  return current;
}

export type ApplyPatchResult = { ok: true; value: unknown } | { ok: false; path: string };

/**
 * Aplica as operacoes, em ordem, sobre uma COPIA (o original nunca muda). Para
 * no primeiro caminho invalido: o patch e atomico, ou entra tudo ou nada.
 */
export function applyDraftPatch(base: unknown, ops: readonly DraftPatchOp[]): ApplyPatchResult {
  const doc = cloneJson(base) as unknown;
  for (const op of ops) {
    const parts = pointerParts(op.path);
    if (!parts || !parts.length) return { ok: false, path: op.path };
    const last = parts[parts.length - 1]!;
    let parent: unknown = doc;
    for (const part of parts.slice(0, -1)) {
      if (Array.isArray(parent) && /^(0|[1-9]\d*)$/.test(part)) parent = parent[Number(part)];
      else if (parent && typeof parent === 'object' && !Array.isArray(parent) && Object.hasOwn(parent, part))
        parent = (parent as Record<string, unknown>)[part];
      else return { ok: false, path: op.path };
    }
    if (Array.isArray(parent)) {
      if (op.op === 'add' && last === '-') parent.push(cloneJson(op.value));
      else {
        if (!/^(0|[1-9]\d*)$/.test(last)) return { ok: false, path: op.path };
        const index = Number(last);
        if (op.op === 'add') {
          if (index > parent.length) return { ok: false, path: op.path };
          parent.splice(index, 0, cloneJson(op.value));
        } else {
          if (index >= parent.length) return { ok: false, path: op.path };
          if (op.op === 'replace') parent[index] = cloneJson(op.value);
          else parent.splice(index, 1);
        }
      }
    } else if (parent && typeof parent === 'object') {
      const record = parent as Record<string, unknown>;
      if (last === '__proto__' || last === 'constructor' || last === 'prototype')
        return { ok: false, path: op.path };
      if (op.op === 'add') record[last] = cloneJson(op.value);
      else {
        if (!Object.hasOwn(record, last)) return { ok: false, path: op.path };
        if (op.op === 'replace') record[last] = cloneJson(op.value);
        else delete record[last];
      }
    } else return { ok: false, path: op.path };
  }
  return { ok: true, value: doc };
}

/**
 * Rebase por campo: o rascunho mudou desde o `baseHash`, mas todo valor que o
 * patch toca continua como o servidor viu. So vale se houver ao menos uma
 * operacao com `expect` e nenhuma `replace`/`remove` sem ele.
 */
export function patchStillApplies(currentDraft: unknown, ops: readonly DraftPatchOp[]): boolean {
  let guarded = false;
  for (const op of ops) {
    if (op.op === 'add') continue;
    if (!('expect' in op)) return false;
    guarded = true;
    if (canonicalJson(getAtPointer(currentDraft, op.path)) !== canonicalJson(op.expect))
      return false;
  }
  return guarded;
}

/** Uma linha do resumo que a pessoa le antes/depois de aplicar. */
export interface DraftPatchSummaryItem {
  /** Caminho do campo (`payments/0/installments`); o rotulo vem da pagina (`labels`). */
  field: string;
  to: unknown;
}

/**
 * O que o painel entrega a pagina (`draft.propose`).
 *
 * `patch` ja foi validado e normalizado pelo servidor; a pagina o valida de novo
 * pelo esquema antes de aplicar. `baseHash` e o `draftHash` do rascunho que o
 * servidor viu ao decidir o patch: se o rascunho da pagina mudou desde entao,
 * a pagina recusa com `conflict` em vez de aplicar por cima.
 */
export interface DraftPatchProposal {
  /** ulid gerado pelo servidor. O painel ignora um `patchId` repetido (idempotencia). */
  patchId: string;
  schema: ScreenDraftSchema;
  baseHash: string;
  patch: DraftPatchOp[];
  summary: DraftPatchSummaryItem[];
  hints?: DraftPatchHints;
}

export type DraftPatchRejectReason =
  | 'no_draft' // a pagina nao tem rascunho (carrinho vazio)
  | 'field_unknown' // o patch toca campo fora do esquema
  | 'conflict' // o rascunho mudou desde o `baseHash`
  | 'sale_done' // a venda ja foi concluida
  | 'review_open' // a revisao da venda esta aberta: nao altera por baixo dela
  | 'invalid' // o resultado nao passa no esquema
  | 'timeout' // ninguem respondeu a tempo (decidido pelo painel)
  | 'stale'; // proposta repetida / pagina trocada (decidido pelo painel)

export interface DraftPatchChange {
  field: string;
  from: unknown;
  to: unknown;
}

/** A pagina aplicou: `resultHash` e o `draftHash` do rascunho depois do patch. */
export interface DraftPatchApplied {
  patchId: string;
  changed: DraftPatchChange[];
  resultHash: string;
}

export interface DraftPatchRejected {
  patchId: string;
  reason: DraftPatchRejectReason;
  /** Para `conflict`/`field_unknown`: o campo. */
  detail?: string;
}

/** A resposta obrigatoria da pagina a cada `draft.propose`: exatamente uma. */
export type DraftPatchAck =
  ({ status: 'applied' } & DraftPatchApplied) | ({ status: 'rejected' } & DraftPatchRejected);

/**
 * Decisao pura que toda pagina toma ao receber uma proposta: o rascunho atual
 * ainda e aquele sobre o qual o servidor decidiu? Funcao unica para que as
 * paginas nao reinventem a regra.
 */
export function checkDraftPatchBase(args: {
  proposal: Pick<DraftPatchProposal, 'patchId' | 'baseHash'> & { patch?: DraftPatchOp[] };
  /** Esquema do rascunho: o hash e calculado sobre a forma normalizada (`draftBaseHash`). */
  schema: ScreenDraftSchema;
  /** `null` quando a pagina nao tem rascunho. */
  currentDraft: unknown;
  alreadyHandled: ReadonlySet<string>;
}): { ok: true; rebased?: true } | { ok: false; reason: DraftPatchRejectReason; duplicate?: true } {
  if (args.alreadyHandled.has(args.proposal.patchId))
    return { ok: false, reason: 'stale', duplicate: true };
  if (args.currentDraft === null || args.currentDraft === undefined)
    return { ok: false, reason: 'no_draft' };
  // Sempre sobre a forma normalizada pelo esquema; nunca o rascunho cru.
  const normalized = draftBaseHash(args.schema, args.currentDraft);
  if (normalized === args.proposal.baseHash) return { ok: true };
  // O carrinho mexe o tempo todo (leitor de codigo): se os campos do patch seguem
  // como o servidor viu, aplica sobre o rascunho de agora (`expect` por campo).
  if (args.proposal.patch && patchStillApplies(args.currentDraft, args.proposal.patch))
    return { ok: true, rebased: true };
  return { ok: false, reason: 'conflict' };
}
